import { IconArrowRight, IconClipboardCheck, IconHistory } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/cn";
import { Badge, Card, Group, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { listarBandejaAutorizacion } from "@/features/bandeja-autorizacion/services/bandeja-service";
import { etiquetaEstado } from "@/lib/estado-labels";
import { TONO_PILL_OUTLINE, tonoDeEstado } from "@/lib/estado-color";
import { formatearMonto } from "@/shared/format/money";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { qk } from "@/lib/query-keys";
import { useAuthStore, useUserPermisos } from "@/lib/auth-store";
import { PERMISOS, VER_SOLICITUDES } from "@/lib/permisos";
import { useActividadReciente } from "../hooks/use-actividad-reciente";
import { useMisEstadisticas } from "../hooks/use-mis-estadisticas";
import type { MiSolicitudDto } from "../services/mis-solicitudes-service";

/**
 * "Mis últimas solicitudes" · timeline corto de las 5 más recientes
 * ordenadas por FechaUltimoEvento descendente.
 *
 * Da contexto inmediato sin tener que abrir /solicitudes · útil sobre todo
 * para volver al hilo después de cerrar el navegador o cambiar de sesión.
 */
export function ActividadRecienteCard() {
  const { data: stats } = useMisEstadisticas();
  const esAutorizador = stats?.audiencia === "Autorizador";
  const esVisor = stats?.audiencia === "Visor";

  // Hooks deben ejecutarse siempre · usamos `enabled` para evitar fetch innecesario.
  // `/solicitudes/mias` responde 403 a quien no tiene permiso de ver solicitudes
  // (autorizadores puros); además para un autorizador la tarjeta renderiza la
  // bandeja y no usa este dato. Gate por permiso, mismo criterio que la ruta.
  const permisos = useUserPermisos();
  const puedeVerSolicitudes = VER_SOLICITUDES.some((p) => permisos.includes(p));
  const misSolicitudes = useActividadReciente(puedeVerSolicitudes && !esAutorizador);
  // Segmentado por usuario (igual que la bandeja) para no mostrar el conteo del
  // usuario anterior al cambiar de sesión en la misma pestaña.
  const usuarioId = useAuthStore((s) => s.user?.id ?? "anon");
  const bandeja = useQuery({
    queryKey: [...qk.bandejaAutorizacion, usuarioId, { page: 1, pageSize: 5 }] as const,
    // La card solo pinta 5 · pedimos justo esa página; `total` da el conteo real.
    queryFn: () => listarBandejaAutorizacion({ page: 1, pageSize: 5 }),
    refetchInterval: 30_000,
    staleTime: 4_000,
    enabled: esAutorizador,
  });

  if (esAutorizador) {
    return (
      <BandejaCard
        data={bandeja.data?.items ?? []}
        total={bandeja.data?.total ?? 0}
        isPending={bandeja.isPending && bandeja.fetchStatus !== "idle"}
      />
    );
  }
  return <MisSolicitudesCard data={misSolicitudes.data ?? []} isPending={misSolicitudes.isPending} esVisor={esVisor} />;
}

function MisSolicitudesCard({
  data,
  isPending,
  esVisor,
}: {
  data: MiSolicitudDto[];
  isPending: boolean;
  esVisor: boolean;
}) {
  const top = data.slice(0, 8);
  // Hotspot 96 · el acceso directo a crear solo con `solicitudes.gestionar`.
  const puedeCrear = useUserPermisos().includes(PERMISOS.solicitudesGestionar);

  return (
    <Card padding="md" className="h-full">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconHistory size={16} stroke={1.8} />
            <Text fw={600} size="sm">
              {esVisor ? "Últimas solicitudes del sistema" : "Mis últimas solicitudes"}
            </Text>
          </Group>
          {data.length > 8 ? (
            <Link to="/solicitudes" className="text-xs text-muted-foreground hover:underline">
              Ver todas ({data.length})
            </Link>
          ) : null}
        </Group>

        {isPending ? (
          <Stack gap="sm">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={42} />
            ))}
          </Stack>
        ) : top.length === 0 ? (
          <Stack gap={4} className="py-2">
            <Text size="sm" c="dimmed" ta="center">
              {esVisor ? "Aún no hay solicitudes en el sistema" : "Aún no tienes solicitudes"}
            </Text>
            {esVisor || !puedeCrear ? null : (
              <Link
                to="/solicitudes/nueva"
                className="text-center text-xs font-medium text-multiva-800 hover:underline dark:text-multiva-400"
              >
                Crear la primera →
              </Link>
            )}
          </Stack>
        ) : (
          <div className="overflow-x-auto">
            <Table verticalSpacing="xs" horizontalSpacing="xs" highlightOnHover>
              <Thead>
                <Tr>
                  <Th>Folio</Th>
                  <Th>Tipo</Th>
                  <Th>Proveedor</Th>
                  <Th align="right">Importe</Th>
                  <Th>Estado</Th>
                  <Th aria-label="Acciones" />
                </Tr>
              </Thead>
              <Tbody>
                {top.map((s) => (
                  <FilaActividad key={s.id} solicitud={s} />
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Stack>
    </Card>
  );
}

function BandejaCard({ data, total, isPending }: { data: BandejaAutorizacionItem[]; total: number; isPending: boolean }) {
  const top5 = data.slice(0, 5);
  return (
    <Card padding="md" className="h-full">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconClipboardCheck size={16} stroke={1.8} />
            <Text fw={600} size="sm">
              Mi bandeja de autorización
            </Text>
          </Group>
          {total > 5 ? (
            <Link to="/autorizacion/bandeja" className="text-xs text-muted-foreground hover:underline">
              Ver todas ({total})
            </Link>
          ) : null}
        </Group>

        {isPending ? (
          <Stack gap="sm">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={42} />
            ))}
          </Stack>
        ) : top5.length === 0 ? (
          <Stack gap={4} className="py-2">
            <Text size="sm" c="dimmed" ta="center">
              Tu bandeja está vacía
            </Text>
            <Text size="xs" c="dimmed" ta="center">
              Aquí aparecerán las solicitudes que requieren tu decisión.
            </Text>
          </Stack>
        ) : (
          <Stack gap="xs">
            {top5.map((s) => (
              <ItemBandeja key={s.id} item={s} />
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

const ICON_LINK_CLASS =
  "flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted";

function ItemBandeja({ item }: { item: BandejaAutorizacionItem }) {
  const cuandoFmt = formatearRelativo(item.fechaUltimoEvento);
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Stack gap={2} className="min-w-0">
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500} ff="mono">
            {item.folio}
          </Text>
          <Badge size="xs" color="info" variant="light">
            Nivel {item.nivelActual}/{item.cantidadNiveles}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" truncate>
          {item.organizacionNombre ?? "Sin organización"} · {cuandoFmt}
        </Text>
      </Stack>
      <Link to={`/autorizacion/${item.id}` as never} aria-label={`Abrir solicitud ${item.folio}`} className={ICON_LINK_CLASS}>
        <IconArrowRight size={16} stroke={1.8} />
      </Link>
    </Group>
  );
}

/** Etiqueta amigable del tipo (null si no hay). */
function etiquetaTipo(tipo: string | null): string | null {
  if (!tipo) return null;
  if (tipo === "ContratoAnualizado") return "Contrato Anualizado";
  if (tipo === "OrdenDeCompra") return "Orden de Compra";
  return tipo;
}

/** Etiqueta amigable del subtipo (null si no hay) · enum con guion bajo → espacios. */
function etiquetaSubtipo(subtipo: string | null): string | null {
  if (!subtipo) return null;
  if (subtipo === "CFDI_UNICO") return "CFDI único";
  return subtipo.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Importe de la solicitud · total del CFDI si existe; si no (p. ej. subtipo
 * Invoice de OC/CA que no carga CFDI), respaldo con el total del prorrateo. La
 * moneda va DESPUÉS del número (ej. "7,581.78 MXN"): formatea sin divisa y anexa
 * el código.
 */
function importeSolicitud(s: MiSolicitudDto): string {
  const total = s.totalCfdi ?? s.totalProrrateo;
  const moneda = s.totalCfdi != null ? s.monedaCfdi : s.monedaProrrateo;
  if (total == null) return "—";
  return moneda ? `${formatearMonto(total, null)} ${moneda}` : formatearMonto(total, null);
}

/** Fila de la tabla "Mis últimas solicitudes". */
function FilaActividad({ solicitud }: { solicitud: MiSolicitudDto }) {
  const label = etiquetaEstado(solicitud.estado, {
    nivelActual: solicitud.flujoNivelActual,
    rolActual: solicitud.flujoRolActual,
  });

  return (
    <Tr>
      <Td className="whitespace-nowrap">
        <Link
          to={`/solicitudes/${solicitud.id}` as never}
          className="block font-mono text-[11px] font-medium text-foreground hover:text-multiva-700 hover:underline dark:hover:text-multiva-400"
        >
          {solicitud.folio}
        </Link>
        <span className="block text-[11px] text-muted-foreground">{formatearRelativo(solicitud.fechaUltimoEvento)}</span>
      </Td>
      <Td className="text-xs text-muted-foreground">
        <span className="block">{etiquetaTipo(solicitud.tipo) ?? "—"}</span>
        {etiquetaSubtipo(solicitud.subtipo) ? (
          <span className="block text-[11px] text-muted-foreground/70">{etiquetaSubtipo(solicitud.subtipo)}</span>
        ) : null}
      </Td>
      <Td className="text-xs text-muted-foreground">
        <span className="block max-w-[68px] truncate" title={solicitud.proveedorRazonSocial ?? undefined}>
          {solicitud.proveedorRazonSocial ?? "—"}
        </span>
      </Td>
      <Td align="right" className="whitespace-nowrap font-mono text-[11px] font-medium text-foreground">
        {importeSolicitud(solicitud)}
      </Td>
      <Td>
        <EstadoPill estado={solicitud.estado} label={label} />
      </Td>
      <Td>
        <Link
          to={`/solicitudes/${solicitud.id}` as never}
          aria-label={`Abrir solicitud ${solicitud.folio}`}
          className={ICON_LINK_CLASS}
        >
          <IconArrowRight size={16} stroke={1.8} />
        </Link>
      </Td>
    </Tr>
  );
}

/**
 * Etiqueta de UNA sola palabra por estado, para el pill compacto de esta card
 * (el label completo, con nivel/rol, se conserva en el `title`/tooltip). Ej.
 * "En autorización segundo nivel" → "Autorización"; "Reglas rechazadas" →
 * "Rechazada"; "Seleccionando proveedor" → "En proceso".
 */
const PILL_PALABRA: Record<string, string> = {
  Iniciada: "Iniciada",
  TipoSeleccionado: "En proceso",
  SubtipoSeleccionado: "En proceso",
  ContratoAnualizadoSeleccionado: "Contrato",
  OrdenDeCompraSeleccionada: "Orden",
  CfdiPendiente: "CFDI",
  CfdiCargado: "CFDI",
  EnValidacionReglas: "Validación",
  ReglasRechazadas: "Rechazada",
  EnValidacionFiscal: "Validación",
  Validado: "Validado",
  RechazadoFiscal: "Rechazada",
  SuspendidaFiscal: "Suspendida",
  CapturandoDatos: "En proceso",
  SeleccionandoProveedor: "En proceso",
  Prorrateando: "Prorrateo",
  ProvisionalmenteListo: "En proceso",
  AdjuntandoDocumentos: "Documentos",
  ListoParaAutorizacion: "Lista",
  EnRevisionDocumental: "Revisión",
  EnAutorizacionNivel1: "Autorización",
  EnAutorizacionNivelN: "Autorización",
  RechazadaParaCorreccion: "Rechazada",
  Autorizada: "Autorizada",
  EnColaContabilizacion: "Contabilización",
  PolizaGenerada: "Póliza",
  EnviadaAContabilizacion: "Contabilización",
  DadaDeBajaPorAutorizador: "Baja",
  CanceladaPorSolicitante: "Cancelada",
  DescartadaPorPlazoVencido: "Vencida",
};

function EstadoPill({ estado, label }: { estado: string; label: string }) {
  // Texto de una palabra · si el estado no está mapeado, cae a la 1ª palabra
  // legible del label completo.
  const palabra = PILL_PALABRA[estado] ?? label.split(" ")[0];
  return (
    <span
      title={label}
      className={cn(
        "inline-flex w-24 shrink-0 items-center justify-center whitespace-nowrap rounded-full border bg-transparent px-2 pt-[3px] pb-[1px] text-[9px] font-bold uppercase leading-none tracking-wider",
        TONO_PILL_OUTLINE[tonoDeEstado(estado)],
      )}
    >
      {palabra}
    </span>
  );
}

// ─── Helpers locales ───────────────────────────────────────────────────────

function formatearRelativo(isoFecha: string): string {
  const ahora = Date.now();
  const fecha = new Date(isoFecha).getTime();
  const diffMs = ahora - fecha;
  const minutos = Math.floor(diffMs / 60_000);
  if (minutos < 1) return "hace un momento";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias < 7) return `hace ${dias} d`;
  return new Date(isoFecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}
